import { useState, useEffect } from "react";
import { OrganizationUserService, OrganizationUserVM, OrganizationUserRolesEnum } from "@propertelligent/client-api";
import { infiniteScrollHook } from "../../../hooks/infiniteScrollHook";
import UserCardItem from "./UserCardItem";
import { useDebounce } from "use-debounce";
import RightSideModal from "../../../newComponents/Modal/right-side-modal";
import Card from "../../../newComponents/card";
import { FormProvider, useForm } from "react-hook-form";
import InputField from "../../../newComponents/form-fields/InputField";
import SelectField from "../../../newComponents/form-fields/SelectField";
import apiHelper from "../../../utils/apiHelper";
import { UserLoginData } from "../../../components/common/charts/Enums";
import ManageUsersTopHeader from "./ManageUsersTopHeader";
import { GetImages } from "../../../utils/GetImages";
import PhoneInput from "../../../newComponents/form-fields/PhoneInput";
import EmailInput from "../../../newComponents/form-fields/EmailInput";
import { PermissionGuard } from "../../../newComponents/PermissionGuard";
import NoAccess from "../../../pages/NoAccess";
import { AccessLevelEnum } from "@propertelligent/client-api";
import { usePermissions } from "../../../hooks/usePermissions";

interface Option {
  Id: string | number;
  Name: string;
}

interface ManageUsersProps {
  className?: string;
}

const ManageUsersPage = ({ className = "" }: ManageUsersProps) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [fullyQualifiedFilter, setFullyQualifiedFilter] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [activeFilter, setActiveFilter] = useState("All");
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<OrganizationUserVM | undefined>(undefined);
  const organizationId = Number(sessionStorage.getItem(UserLoginData.organization_id));

  const check = usePermissions();
  const canWrite = check.canWrite("ManageUsers");

  // For Empty State
  const [hasAnyUsers, setHasAnyUsers] = useState<boolean>(false);

  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

  const [userRoles, setUserRoles] = useState<Option[]>([]);

  const {
    rows,
    loading,
    hasMore,
    rowCount,
    fetchData,
    currentPage,
  } = infiniteScrollHook({
    httpFetchDataFn: OrganizationUserService.getApiOrganizationUserOrganizationlist,
    FullyQualifiedFilters: fullyQualifiedFilter,
    FullyQualifiedSorts: `User.FirstName ${sortDirection}`,
    pageSize: 10,
  });

  const fetchUserRoles = () => {
    OrganizationUserService.getApiOrganizationUserUserRoles()
      .then((res) => {
        if (res?.success) {
          const mappedRoles = res.success.map((role: any) => ({
            Id: role.id,
            Name: role.name,
          }));
          setUserRoles(mappedRoles);
        }
      })
      .catch((err) => {
        console.error("Error fetching user roles:", err);
      });
  };

  useEffect(() => {
    fetchUserRoles();
    if (searchTerm !== "") {
      setFullyQualifiedFilter(
        `(User.FirstName + " " + User.LastName).ToLower().Contains("${searchTerm.toLowerCase()}")`
      );
    } else {
      setFullyQualifiedFilter(null);
    }
  }, [searchTerm]);

  // Sort change handler
  const handleSortChange = (direction: "asc" | "desc") => {
    setSortDirection(direction);
  };

  useEffect(() => {
    if (rowCount > 0) {
      setHasAnyUsers(true);
    }
  }, [rowCount]);

  const methods = useForm<OrganizationUserVM>({
    defaultValues: {
      id: undefined,
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      title: "",
      organizationUserTypeId: OrganizationUserRolesEnum.Admin,
      organizationId: organizationId,
    },
    mode: "onSubmit",
  });

  const { handleSubmit, reset, formState: { isDirty } } = methods;

  const onSubmit = async (data: OrganizationUserVM) => {
    try {
      const isEdit = !!data.id;

      // ----- PAYLOAD SETUP -----
      const payload = {

        ...(isEdit && { id: data.id }),
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        title: data.title,
        organizationUserTypeId: data.organizationUserTypeId,
        organizationId: organizationId,
        ...(isEdit && { organizationUserId: data.organizationUserId }) // only for PUT
      };


      if (isEdit) {
        const res = await apiHelper(OrganizationUserService.putApiOrganizationUser, {
          title: "User Updated",
          successMessage: "User update successfully",
          failureMessage: "Unable to update user",
          showNotification: true,
          successIcon: <img src={GetImages.toastUserIcon} alt="success" width="20" height="20" />
        }, data.organizationUserId, payload);

        if (res?.data) {
          setAddModalOpen(false);
          reset();
          fetchData(1);
        }
      } else {
        const res = await apiHelper(OrganizationUserService.postApiOrganizationUser, {
          title: "User Added",
          successMessage: "User added successfully",
          failureMessage: "Unable to add user",
          showNotification: true,
          successIcon: <img src={GetImages.toastUserIcon} alt="success" width="20" height="20" />
        }, payload);

        if (res?.data) {
          setAddModalOpen(false);
          reset();
          fetchData(1);
        }
      }

    } catch (error) {
      console.error("Error submitting user:", error);
    }
  };


  const handleEditUser = (user: OrganizationUserVM) => {
    setSelectedUser(user);
    methods.reset(user);
    setAddModalOpen(true);
  };

  const handleModalClose = () => {
    setAddModalOpen(false);
    setSelectedUser(undefined);
    reset({
      id: undefined,
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      title: "",
      organizationUserTypeId: OrganizationUserRolesEnum.Admin,
      organizationId: organizationId,
    });
  };

  const formId = selectedUser ? "edit-user-form" : "add-user-form";

  return (
    <PermissionGuard permission="ManageUsers" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
      <div className={`manage-users-page content-wrapper container-fluid ${className}`}>
        {hasAnyUsers && (
          <ManageUsersTopHeader
            PageHeading="Users"
            Count={rowCount}
            ButtonName={canWrite ? "Add User" : undefined}
            MobileButtonName={canWrite ? "Add" : undefined}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onClick={() => {
              setSelectedUser(undefined);
              reset();
              setAddModalOpen(true);
            }}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
          />
        )}

        <UserCardItem
          dataHookState={{
            rows,
            loading,
            hasMore,
            fetchData,
            currentPage,
          }}
          onEditUser={canWrite ? handleEditUser : undefined}
          className="organisation-listing-card"
          hasAnyUsers={hasAnyUsers}
        />

        <RightSideModal
          isOpen={isAddModalOpen}
          onClose={handleModalClose}
          title={selectedUser ? "Edit User" : "Add User"}
          buttonlabel={selectedUser ? "Update User" : "Add User"}
          formId={formId}
          maxWidth={550}
          isDirty={isDirty}
        >
          <Card className="no-bg">
            <FormProvider {...methods}>
              <form id={formId} onSubmit={handleSubmit(onSubmit, (error) => {
                console.log("Form validation errors:", error);
              })}>
                <div className="row">
                  <div className="col-12">
                    <SelectField
                      placeholderText="Select User Role"
                      name="organizationUserTypeId"
                      label="User Role"
                      required
                      requiredMessage="User role is required"
                      options={userRoles}
                    />
                  </div>
                  <div className="col-12">
                    <InputField
                      name="title"
                      label="Title"
                      type="text"
                      placeholder="Enter Title"
                      required
                      requiredMessage="Title is required"
                      onlyAlphabets
                      allowSpecialChars={false}
                    />
                  </div>
                  <div className="col-md-6">
                    <InputField
                      name="firstName"
                      label="First Name"
                      type="text"
                      required
                      requiredMessage="First Name is required"
                      placeholder="Enter First Name"
                      onlyAlphabets
                      allowSpecialChars={false}
                      validation={{
                        minLength: {
                          value: 2,
                          message: "First name must be at least 2 characters long"
                        },
                        maxLength: {
                          value: 20,
                          message: "First name cannot exceed 20 characters"
                        }
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <InputField
                      name="lastName"
                      label="Last Name"
                      type="text"
                      required
                      requiredMessage="Last Name is required"
                      placeholder="Enter Last Name"
                      onlyAlphabets
                      allowSpecialChars={false}
                      validation={{
                        minLength: {
                          value: 2,
                          message: "Last name must be at least 2 characters long"
                        },
                        maxLength: {
                          value: 20,
                          message: "Last name cannot exceed 20 characters"
                        }
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <EmailInput
                      name="email"
                      label="Email Address"
                      required
                      requiredMessage="Email Address is required"
                      placeholder="Enter Email Address"
                    />
                  </div>
                  <div className="col-md-6">
                    <PhoneInput
                      name="phoneNumber"
                      label="Contact No"
                      required={true}
                      requiredMessage="Contact No is required"
                      defaultCountry="us"
                      placeholder="+1 (555) 000-0000"
                    />

                  </div>
                </div>
              </form>
            </FormProvider>
          </Card>
        </RightSideModal>
      </div>
    </PermissionGuard>
  );
};

export default ManageUsersPage;